<template>
  <section class="featured-carousel">
    <h2 class="featured-carousel__heading">主题展览</h2>

    <div class="featured-carousel__wrap">
      <button
        type="button"
        class="featured-carousel__nav featured-carousel__nav--prev"
        aria-label="上一个"
        @click="prev"
      >
        <i class="el-icon-arrow-left" />
      </button>

      <div class="featured-carousel__track">
        <div
          v-for="(item, index) in items"
          :key="item.id"
          class="featured-carousel__slide"
          :class="getSlideClass(index)"
          @click="handleSlideClick(item, index)"
        >
          <div
            class="featured-carousel__card"
            :class="{ 'featured-carousel__card--active': currentIndex === index }"
          >
            <div class="featured-carousel__image">
              <img :src="item.image" :alt="item.title" />
            </div>
            <div class="featured-carousel__caption">
              {{ item.title }}
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        class="featured-carousel__nav featured-carousel__nav--next"
        aria-label="下一个"
        @click="next"
      >
        <i class="el-icon-arrow-right" />
      </button>
    </div>

    <div class="featured-carousel__dots">
      <button
        v-for="(_, index) in items"
        :key="index"
        type="button"
        class="featured-carousel__dot"
        :class="{ 'featured-carousel__dot--active': currentIndex === index }"
        @click="goTo(index)"
      />
    </div>
  </section>
</template>

<script>
export default {
  name: 'FeaturedCarousel',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    autoplay: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentIndex: 0,
      timer: null
    }
  },
  mounted() {
    this.startAutoplay()
  },
  beforeDestroy() {
    this.stopAutoplay()
  },
  methods: {
    getSlideClass(index) {
      const len = this.items.length
      if (!len) return ''

      const prev = (this.currentIndex - 1 + len) % len
      const next = (this.currentIndex + 1) % len

      if (index === this.currentIndex) return 'featured-carousel__slide--active'
      if (index === prev) return 'featured-carousel__slide--prev'
      if (index === next) return 'featured-carousel__slide--next'
      return 'featured-carousel__slide--hidden'
    },
    prev() {
      const len = this.items.length
      if (!len) return
      this.currentIndex = (this.currentIndex - 1 + len) % len
    },
    next() {
      const len = this.items.length
      if (!len) return
      this.currentIndex = (this.currentIndex + 1) % len
    },
    goTo(index) {
      this.currentIndex = index
    },
    handleSlideClick(item, index) {
      if (index !== this.currentIndex) {
        this.goTo(index)
        return
      }
      if (item && item.id) {
        this.$router.push(`/exhibition/${item.id}`)
      }
    },
    startAutoplay() {
      if (!this.autoplay || this.items.length <= 1) return
      this.timer = setInterval(this.next, 9000)
    },
    stopAutoplay() {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.featured-carousel {
  padding: $spacing-2xl $content-padding;
  max-width: $container-max-width;
  margin: 0 auto;

  &__heading {
    font-family: $font-serif;
    font-size: rem(24);
    font-weight: 700;
    color: $color-brown-dark;
    text-align: center;
    margin-bottom: $spacing-xl;
    letter-spacing: 4px;
  }

  &__wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 $spacing-3xl;
  }

  &__track {
    position: relative;
    width: 100%;
    max-width: 100%;
    height: rem(380);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__slide {
    position: absolute;
    transition: all 0.6s $ease-apple;
    cursor: pointer;

    &--active {
      z-index: 3;
      transform: scale(1);
      opacity: 1;
    }

    &--prev {
      z-index: 2;
      transform: translateX(-62%) scale(0.75);
      opacity: 0.6;
    }

    &--next {
      z-index: 2;
      transform: translateX(62%) scale(0.75);
      opacity: 0.6;
    }

    &--hidden {
      z-index: 1;
      transform: scale(0.5);
      opacity: 0;
      pointer-events: none;
    }
  }

  &__card {
    width: rem(720);
    overflow: hidden;
    border-radius: 4px;
    box-shadow: 0 12px 40px rgba($color-brown-dark, 0.2);
    transition: box-shadow 0.25s ease, transform 0.25s ease;

    &--active {
      cursor: pointer;

      &:hover {
        box-shadow: 0 16px 48px rgba($color-brown-dark, 0.28);
        transform: translateY(rem(-4));

        .featured-carousel__image img {
          transform: scale(1.03);
        }
      }
    }
  }

  &__image {
    height: rem(280);
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      filter: grayscale(0.6) sepia(0.2);
      transition: transform 0.35s ease;
    }
  }

  &__caption {
    padding: $spacing-md;
    background: $color-brown-dark;
    color: $color-white;
    font-family: $font-serif;
    font-size: rem(16);
    text-align: center;
    letter-spacing: 2px;
  }

  &__nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 4;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: rgba($color-brown-dark, 0.85);
    color: $color-white;
    font-size: rem(18);
    transition: background 0.2s;

    &:hover {
      background: $success-color;
    }

    &--prev {
      left: 0;
    }

    &--next {
      right: 0;
    }
  }

  &__dots {
    display: flex;
    justify-content: center;
    gap: $spacing-sm;
    margin-top: $spacing-base;
  }

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: rgba($color-brown-dark, 0.25);
    transition: all 0.3s;

    &--active {
      background: $success-color;
      width: 24px;
      border-radius: 4px;
    }
  }
}

@media (max-width: 768px) {
  .featured-carousel {
    &__track {
      height: rem(320);
    }

    &__card {
      width: min(92vw, rem(560));
    }

    &__image {
      height: rem(360);
    }

    &__slide--prev {
      transform: translateX(-50%) scale(0.7);
    }

    &__slide--next {
      transform: translateX(50%) scale(0.7);
    }
  }
}
</style>
